import React from 'react';
import { PenLine, MousePointer2, Save, Trash2, Undo2, Check, Circle, Minus, Crosshair } from 'lucide-react';
import { formatSk, skColor } from './fiSnowDigitizerGeometry';

export default function ContourToolPanel({
  readOnly = false,
  skLevels,
  skColors,
  activeSk,
  setActiveSk,
  lineKind,
  setLineKind,
  toolMode,
  setToolMode,
  contours,
  selectedId,
  onSelectContour,
  draftPoints,
  onFinishDraft,
  onCancelDraft,
  onDeleteSelected,
  onUndo,
  onSave,
  saving,
  saveMessage,
  error,
  probeHover,
  probePins,
  onClearProbePins,
  onRemoveProbePin,
}) {
  const selected = contours.find((c) => c.id === selectedId);

  return (
    <aside className="fsd-panel">
      <div>
        <div className="fsd-title">FI Snow Isolines</div>
        <div className="fsd-subtitle">
          {readOnly
            ? 'YM 6/16 ground snow map is finalized and locked. Contours are view-only; use Probe to sample s_k.'
            : 'Digitize YM 6/16 ground snow contours. Draw mode snaps to existing nodes and edges. Select mode: click a segment to add a point, right-click a node to remove it.'}
        </div>
      </div>

      {readOnly && (
        <div className="fsd-success">
          Digitization locked — current map state is preserved for deployment.
        </div>
      )}
      {error && <div className="fsd-error">{error}</div>}
      {saveMessage && <div className="fsd-success">{saveMessage}</div>}

      {!readOnly && (
        <div>
          <div className="fsd-section-title">s_k level (kN/m²)</div>
          <select
            className="fsd-select"
            value={activeSk}
            onChange={(e) => setActiveSk(parseFloat(e.target.value))}
          >
            {(skLevels || [2, 2.5, 2.75, 3, 3.5]).map((sk) => (
              <option key={sk} value={sk}>
                {formatSk(sk)}
              </option>
            ))}
          </select>
          <div className="fsd-legend" style={{ marginTop: '0.5rem' }}>
            {(skLevels || []).map((sk) => (
              <span key={sk} className="fsd-legend-item">
                <span className="fsd-swatch" style={{ background: skColors?.[String(sk)] || skColor(sk) }} />
                {formatSk(sk)}
              </span>
            ))}
          </div>
        </div>
      )}

      {readOnly && (
        <div className="fsd-legend">
          {(skLevels || []).map((sk) => (
            <span key={sk} className="fsd-legend-item">
              <span className="fsd-swatch" style={{ background: skColors?.[String(sk)] || skColor(sk) }} />
              {formatSk(sk)}
            </span>
          ))}
        </div>
      )}

      {!readOnly && (
        <div>
          <div className="fsd-section-title">Line type</div>
          <div className="fsd-toggle-group" style={{ gridTemplateColumns: '1fr' }}>
            <button
              type="button"
              className={`fsd-toggle-btn${lineKind === 'open' ? ' active' : ''}`}
              onClick={() => setLineKind('open')}
            >
              <Minus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Open isoline
            </button>
            <button
              type="button"
              className={`fsd-toggle-btn${lineKind === 'closed_isoline' ? ' active' : ''}`}
              onClick={() => setLineKind('closed_isoline')}
            >
              <Circle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Closed interpolation line
            </button>
            <button
              type="button"
              className={`fsd-toggle-btn${lineKind === 'closed_zone' ? ' active' : ''}`}
              onClick={() => setLineKind('closed_zone')}
            >
              <Circle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Closed region (constant s_k)
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="fsd-section-title">Tool</div>
        {readOnly ? (
          <div className="fsd-toggle-group" style={{ gridTemplateColumns: '1fr' }}>
            <button type="button" className="fsd-toggle-btn active" disabled>
              <Crosshair size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Probe
            </button>
          </div>
        ) : (
          <div className="fsd-toggle-group" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <button
              type="button"
              className={`fsd-toggle-btn${toolMode === 'draw' ? ' active' : ''}`}
              onClick={() => setToolMode('draw')}
            >
              <PenLine size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Draw
            </button>
            <button
              type="button"
              className={`fsd-toggle-btn${toolMode === 'select' ? ' active' : ''}`}
              onClick={() => setToolMode('select')}
            >
              <MousePointer2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Select
            </button>
            <button
              type="button"
              className={`fsd-toggle-btn${toolMode === 'probe' ? ' active' : ''}`}
              onClick={() => setToolMode('probe')}
            >
              <Crosshair size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Probe
            </button>
          </div>
        )}
      </div>

      {toolMode === 'probe' && (
        <div>
          <div className="fsd-section-title">s_k probe</div>
          <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.45, marginBottom: '0.5rem' }}>
            {readOnly
              ? 'Hover for interpolated value from the saved map. Click to pin a measurement marker; right-click a pin to remove.'
              : 'Hover for interpolated value from current contours. Click to pin a measurement marker; right-click a pin to remove.'}
          </div>
          {probeHover && (
            <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              {probeHover.loading && <span style={{ color: '#94a3b8' }}>Sampling…</span>}
              {!probeHover.loading && probeHover.error && (
                <span style={{ color: '#fca5a5' }}>{probeHover.error}</span>
              )}
              {!probeHover.loading && !probeHover.error && probeHover.sk_kN_m2 != null && (
                <span>
                  Cursor: <span className="fsd-probe-pin-value">{probeHover.sk_kN_m2} kN/m²</span>
                  {probeHover.lat != null && (
                    <span style={{ color: '#64748b', marginLeft: 6 }}>
                      ({probeHover.lat.toFixed?.(3) ?? probeHover.lat}°, {probeHover.lon.toFixed?.(3) ?? probeHover.lon}°)
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
          {probePins?.length > 0 && (
            <>
              <div className="fsd-probe-pin-list">
                {probePins.map((pin, idx) => (
                  <div key={pin.id} className="fsd-probe-pin-item">
                    <span>
                      #{idx + 1}{' '}
                      <span className="fsd-probe-pin-value">{pin.sk_kN_m2} kN/m²</span>
                    </span>
                    <button type="button" className="fsd-btn" style={{ padding: '0.15rem 0.4rem', fontSize: '0.72rem' }} onClick={() => onRemoveProbePin?.(pin.id)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="fsd-btn" style={{ marginTop: '0.4rem' }} onClick={onClearProbePins}>
                Clear pins
              </button>
            </>
          )}
        </div>
      )}

      {!readOnly && toolMode === 'draw' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <button
            type="button"
            className="fsd-btn fsd-btn-primary"
            disabled={draftPoints.length < 2}
            onClick={onFinishDraft}
          >
            <Check size={15} />
            {lineKind === 'open' ? 'Finish line' : 'Close & finish'}
          </button>
          <button type="button" className="fsd-btn" disabled={!draftPoints.length} onClick={onCancelDraft}>
            Cancel draft ({draftPoints.length} pts)
          </button>
        </div>
      )}

      <div>
        <div className="fsd-section-title">Contours ({contours.length})</div>
        <div className="fsd-contour-list">
          {contours.length === 0 && (
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
              {readOnly ? 'No contours loaded.' : 'No contours yet — switch to Draw and click on the map.'}
            </div>
          )}
          {contours.map((c) => (
            <div
              key={c.id}
              className={`fsd-contour-item${c.id === selectedId ? ' selected' : ''}`}
              onClick={() => !readOnly && onSelectContour(c.id)}
              style={readOnly ? { cursor: 'default' } : undefined}
            >
              <span className="fsd-swatch" style={{ background: skColor(c.sk_kN_m2) }} />
              <span>
                {formatSk(c.sk_kN_m2)} · {c.role === 'zone' ? 'zone' : c.closed ? 'closed line' : 'open'} · {c.points.length} pts
              </span>
            </div>
          ))}
        </div>
      </div>

      {!readOnly && selected && (
        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
          Selected: {formatSk(selected.sk_kN_m2)}{' '}
          {selected.role === 'zone' ? 'closed region' : selected.closed ? 'closed interpolation line' : 'open line'} (
          {selected.points.length} points)
        </div>
      )}

      {!readOnly && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: 'auto' }}>
          <button type="button" className="fsd-btn" onClick={onUndo}>
            <Undo2 size={15} />
            Undo (Ctrl+Z)
          </button>
          <button
            type="button"
            className="fsd-btn fsd-btn-danger"
            disabled={!selectedId}
            onClick={onDeleteSelected}
          >
            <Trash2 size={15} />
            Delete selected
          </button>
          <button type="button" className="fsd-btn fsd-btn-primary" disabled={saving} onClick={onSave}>
            <Save size={15} />
            {saving ? 'Saving…' : 'Save & rebuild grid'}
          </button>
        </div>
      )}
    </aside>
  );
}
